import { useState } from 'react'
import { canReadArea, isBoss, type PublicUserDTO } from '@wf/shared'
import { useAttendanceToday } from '../attendance/useAttendance'
import { WhoIsInPanel } from '../attendance/WhoIsInPanel'
import { formatDateDE, formatHMS } from '../time/format'
import { WorkplanEditor } from './WorkplanEditor'
import { liveWorkedSeconds, useDayRolloverInvalidation, useNow } from './live'
import { shiftDay } from './timeMath'
import { buildDaySegments, timelinePct, wallMinuteOfDay, TIMELINE_END_MIN, TIMELINE_START_MIN } from './timeline'
import { useTeamReports } from './useZeiten'

/** Dienstplan verwalten (deckungsgleich mit dem Server-Guard canManageWorkPlans). */
function canManagePlans(user: PublicUserDTO): boolean {
  return user.isSuperadmin || canReadArea(user, 'arbeitszeit')
}

const AXIS = ['06:00', '08:00', '10:00', '12:00', '14:00', '16:00', '18:00']
const STATUS_LABEL: Record<string, string> = { an: 'Anwesend', pause: 'Pause', aus: 'Gegangen' }

function initials(name: string): string {
  return name
    .split(' ')
    .map((p) => p[0])
    .join('')
    .replace('.', '')
    .slice(0, 2)
    .toUpperCase()
}

/**
 * „Zeiterfassung · Heute" nach Simons Design (1b): KPI-Karten (Anwesend/Pause/Gegangen/Ist-Stunden;
 * „Abwesend/Krank" gibt es als Backend-Status noch nicht) + Tages-Timeline je Mitarbeiter (06–18 Uhr,
 * SVG, Jetzt-Linie). Timeline nur isBoss (fremde Reports); mit seesAllOrders ohne Boss-Position bleibt
 * das bewährte „Wer ist da?"-Panel. Darunter: Dienstplan-Editor (planung|chef|einstellungen).
 */
export function HeutePanel({ user, onCorrect }: { user: PublicUserDTO; onCorrect: (userId: number) => void }) {
  const team = useAttendanceToday()
  const now = useNow()
  const today = useDayRolloverInvalidation() // Werkstatt-Tag; invalidiert Snapshot+Reports beim Umspringen
  const boss = isBoss(user)
  const staff = team.data?.entries ?? []
  // 2-Tage-Fenster (gestern..heute): eine Nachtschicht beginnt mit einem VORTAGS-Kommen — nur mit ihm
  // kann die Timeline das hereinlaufende Segment zeichnen (Review-Fund; Segment-Minuten relativ zu heute).
  const reports = useTeamReports(staff.map((e) => e.userId), shiftDay(today, -1), today, boss && staff.length > 0)
  const [planUserId, setPlanUserId] = useState<number | null>(null)

  const counts = { an: 0, pause: 0, aus: 0 }
  let totalIst = 0
  for (const e of staff) {
    counts[e.status] = (counts[e.status] ?? 0) + 1
    totalIst += liveWorkedSeconds(e, now)
  }
  const nowMin = wallMinuteOfDay(now)
  const nowVisible = nowMin >= TIMELINE_START_MIN && nowMin <= TIMELINE_END_MIN

  return (
    <div className="wf-view wf-zstack">
      {/* Bewusst OHNE große Live-Uhr (Doppelung; die Jetzt-Linie der Timeline zeigt die Zeit ohnehin). */}
      <div className="wf-zshead">
        <div>
          <div className="wf-zshead__greet">Zeiterfassung · Heute</div>
          <div className="wf-zshead__date">{formatDateDE(today)}</div>
        </div>
      </div>

      {team.isLoading ? <div className="muted">Team wird geladen…</div> : null}
      {team.isError ? (
        <div className="error" role="alert">
          Team-Anwesenheit konnte nicht geladen werden.
        </div>
      ) : null}
      {team.data && staff.length === 0 ? (
        <div className="wf-empty">
          <div className="wf-empty__title">Noch keine Mitarbeiter</div>
          <div className="wf-empty__text">Lade dein Team über Einstellungen → Team ein.</div>
        </div>
      ) : null}

      {staff.length > 0 ? (
        <div className="grid cards4 wf-zskpis">
          <div className="wf-stat wf-stat--fertig">
            <div className="wf-stat__label">Anwesend</div>
            <div className="wf-stat__value">{counts.an}</div>
          </div>
          <div className="wf-stat wf-stat--arbeit">
            <div className="wf-stat__label">In Pause</div>
            <div className="wf-stat__value">{counts.pause}</div>
          </div>
          <div className="wf-stat">
            <div className="wf-stat__label">Gegangen / nicht da</div>
            <div className="wf-stat__value">{counts.aus}</div>
          </div>
          <div className="wf-stat wf-stat--offen">
            <div className="wf-stat__label">Ist-Stunden heute</div>
            <div className="wf-stat__value wf-mono">{formatHMS(totalIst)}</div>
          </div>
        </div>
      ) : null}

      {boss && staff.length > 0 ? (
        <div className="wf-zstl">
          <div className="wf-zstl__head">
            <span className="wf-zstl__who">Mitarbeiter</span>
            <span className="wf-zstl__axis">
              {AXIS.map((t) => (
                <span key={t} className="wf-mono">
                  {t}
                </span>
              ))}
            </span>
            <span className="wf-zstl__ist">Ist</span>
            <span className="wf-zstl__edit" />
          </div>
          {staff.map((entry, i) => {
            const report = reports[i]?.data
            const loading = reports[i]?.isLoading ?? false
            const segs = report ? buildDaySegments(report.stamps, nowMin, today) : []
            return (
              <div key={entry.userId} className="wf-zstl__row">
                <span className="wf-zstl__who">
                  <span className="wf-zstl__avatar" aria-hidden="true">
                    {initials(entry.name)}
                  </span>
                  <span className="wf-zstl__name">{entry.name}</span>
                  <span className={`wf-zspill wf-zspill--${entry.status} wf-zspill--sm`}>
                    <span className="wf-zspill__dot" aria-hidden="true" />
                    {STATUS_LABEL[entry.status] ?? entry.status}
                  </span>
                </span>
                <span className="wf-zstl__track">
                  {loading ? <span className="muted">…</span> : null}
                  {reports[i]?.isError ? <span className="error">Tagesverlauf nicht ladbar</span> : null}
                  <svg width="100%" height="28" aria-label={`Tagesverlauf ${entry.name}`}>
                    <rect className="wf-zstl__bg" x="0" y="0" width="100%" height="28" rx="6" />
                    {segs.map((s, k) => (
                      <rect
                        key={k}
                        className={
                          s.kind === 'work'
                            ? s.open
                              ? 'wf-zstl__work wf-zstl__work--open'
                              : 'wf-zstl__work'
                            : 'wf-zstl__pause'
                        }
                        x={`${timelinePct(s.startMin)}%`}
                        width={`${Math.max(0.4, timelinePct(s.endMin) - timelinePct(s.startMin))}%`}
                        y={s.kind === 'work' ? 7 : 10}
                        height={s.kind === 'work' ? 14 : 8}
                        rx="3"
                      />
                    ))}
                    {nowVisible ? <rect className="wf-zstl__now" x={`${timelinePct(nowMin)}%`} y="0" width="2" height="28" /> : null}
                  </svg>
                </span>
                <span className="wf-zstl__ist wf-mono">{liveWorkedSeconds(entry, now) ? formatHMS(liveWorkedSeconds(entry, now)) : '–'}</span>
                <span className="wf-zstl__edit">
                  <button
                    type="button"
                    className="wf-btn wf-btn--ghost wf-btn--sm"
                    aria-label={`Buchungen von ${entry.name} korrigieren`}
                    title="Buchung korrigieren (Nachweis)"
                    onClick={() => onCorrect(entry.userId)}
                  >
                    Korrigieren
                  </button>
                </span>
              </div>
            )
          })}
          <div className="wf-zstl__legend">
            <span>
              <span className="wf-zstl__lg wf-zstl__lg--work" aria-hidden="true" /> Arbeitszeit
            </span>
            <span>
              <span className="wf-zstl__lg wf-zstl__lg--pause" aria-hidden="true" /> Pause
            </span>
            {nowVisible ? (
              <span>
                <span className="wf-zstl__lg wf-zstl__lg--now" aria-hidden="true" /> Jetzt
              </span>
            ) : null}
            <span className="wf-zstl__legend-hint">„Korrigieren" öffnet den Monatsnachweis</span>
          </div>
        </div>
      ) : null}

      {!boss ? <WhoIsInPanel /> : null}

      {canManagePlans(user) ? (
        <>
          <div className="wf-sectionhead">
            <h3>Dienstplan bearbeiten</h3>
            <select
              className="wf-input"
              aria-label="Mitarbeiter für Dienstplan wählen"
              value={planUserId ?? ''}
              onChange={(e) => setPlanUserId(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">Mitarbeiter wählen…</option>
              {staff.map((entry) => (
                <option key={entry.userId} value={entry.userId}>
                  {entry.name}
                </option>
              ))}
            </select>
          </div>
          {planUserId != null ? (
            <WorkplanEditor key={planUserId} userId={planUserId} userName={staff.find((e) => e.userId === planUserId)?.name ?? ''} />
          ) : null}
        </>
      ) : null}
    </div>
  )
}
